import {
  signInWithGoogle,
  signInWithKakao,
  signInWithNaver,
  signInWithGoogleRedirect,
  signInWithKakaoRedirect,
  signInWithNaverRedirect,
  firebaseGetRedirectResult,
} from "@/lib/api/firebase-ultra-safe";
import { User } from "firebase/auth";
import { doc, setDoc, getDoc, collection, addDoc } from "firebase/firestore";
import { getFirebaseDb } from "@/lib/api/firebase-ultra-safe";
import { logAuthAction } from "@/lib/api/user-actions";
import { isMobile } from "@/lib/utils";

// Firebase 인스턴스 가져오기
const getDb = getFirebaseDb;

export interface SNSUserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  provider: "google" | "kakao" | "naver";
  providerId: string;
}

/**
 * SNS 로그인 결과를 처리하고 사용자 프로필을 생성/업데이트합니다.
 */
export const handleSNSLogin = async (
  user: User,
  provider: "google" | "kakao" | "naver"
): Promise<SNSUserProfile> => {
  try {
    const db = await getDb();
    // 사용자 프로필 정보 추출
    const profile: SNSUserProfile = {
      uid: user.uid,
      email: user.email || "",
      displayName: user.displayName || user.email?.split("@")[0] || "사용자",
      photoURL: user.photoURL || undefined,
      provider,
      providerId: user.providerData[0]?.uid || user.uid,
    };

    // Firestore에 사용자 프로필 저장/업데이트
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      // 기존 사용자 - 프로필 업데이트
      await setDoc(
        userRef,
        {
          ...userSnap.data(),
          displayName: profile.displayName,
          photoURL: profile.photoURL,
          lastLoginAt: new Date(),
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // 기존 사용자 로그인 액션 로그
      await logAuthAction(profile.uid, `${provider}_login_existing`, {
        provider,
        displayName: profile.displayName,
        email: profile.email,
      });
    } else {
      // 신규 사용자 - 프로필 생성
      await setDoc(userRef, {
        uid: profile.uid,
        email: profile.email,
        displayName: profile.displayName,
        photoURL: profile.photoURL,
        provider: profile.provider,
        providerId: profile.providerId,
        points: 5000, // 회원가입 포인트 지급
        createdAt: new Date(),
        lastLoginAt: new Date(),
        updatedAt: new Date(),
        // 기본 설정
        isEmailVerified: true, // SNS 로그인은 이메일이 이미 인증됨
        role: "user",
        status: "active",
      });

      // 포인트 지급 내역 기록 (초기 잔액 5,000P)
      try {
        const db = await getDb();
        const pointHistoryRef = collection(db, "pointHistory");
        await addDoc(pointHistoryRef, {
          userId: profile.uid,
          amount: 5000,
          type: "signup",
          description: "🎉 회원가입 축하 포인트",
          balance: 5000,
          createdAt: new Date(),
        });
      } catch (e) {
        console.error("SNS 회원가입 포인트 기록 실패:", e);
      }

      // 신규 사용자 가입 액션 로그
      await logAuthAction(profile.uid, `${provider}_signup_new`, {
        provider,
        displayName: profile.displayName,
        email: profile.email,
      });
    }

    return profile;
  } catch (error) {
    console.error("SNS 로그인 처리 중 오류:", error);
    throw new Error("SNS 로그인 처리에 실패했습니다.");
  }
};

/**
 * 구글 로그인 (모바일에서는 redirect, 데스크톱에서는 popup 사용)
 */
export const loginWithGoogle = async (): Promise<SNSUserProfile | void> => {
  try {
    // 모바일 환경에서는 redirect 방식 사용
    if (isMobile()) {
      console.log("📱 모바일 환경 감지 - redirect 방식으로 로그인");
      await signInWithGoogleRedirect();
      // redirect 방식이므로 void를 반환 (콜백 페이지에서 처리)
      return;
    }

    // 데스크톱에서는 popup 방식 사용
    console.log("💻 데스크톱 환경 - popup 방식으로 로그인");
    const result = await signInWithGoogle();
    return await handleSNSLogin(result.user, "google");
  } catch (error: any) {
    console.error("구글 로그인 오류:", error);

    // Firebase Auth 에러 코드별 메시지
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("로그인 창이 닫혔습니다. 다시 시도해주세요.");
    } else if (error.code === "auth/popup-blocked") {
      // 팝업이 차단되면 redirect 방식으로 재시도
      console.log("⚠️ 팝업 차단 감지 - redirect 방식으로 재시도");
      try {
        await signInWithGoogleRedirect();
        return;
      } catch (redirectError) {
        throw new Error(
          "로그인에 실패했습니다. 팝업을 허용하거나 다른 방식으로 로그인해주세요."
        );
      }
    } else if (error.code === "auth/network-request-failed") {
      throw new Error("네트워크 연결을 확인해주세요.");
    } else if (error.code === "auth/too-many-requests") {
      throw new Error(
        "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요."
      );
    } else if (error.code === "auth/unauthorized-domain") {
      throw new Error(
        "인증되지 않은 도메인입니다. Firebase 콘솔에서 도메인을 추가해주세요."
      );
    }

    throw new Error("구글 로그인에 실패했습니다.");
  }
};

/**
 * 카카오 로그인
 */
export const loginWithKakao = async (): Promise<SNSUserProfile> => {
  try {
    const result = await signInWithKakao();
    return await handleSNSLogin(result.user, "kakao");
  } catch (error: any) {
    console.error("카카오 로그인 오류:", error);

    // Firebase Auth 에러 코드별 메시지
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("로그인 창이 닫혔습니다. 다시 시도해주세요.");
    } else if (error.code === "auth/popup-blocked") {
      throw new Error(
        "팝업이 차단되었습니다. 팝업을 허용하고 다시 시도해주세요."
      );
    } else if (error.code === "auth/network-request-failed") {
      throw new Error("네트워크 연결을 확인해주세요.");
    } else if (error.code === "auth/too-many-requests") {
      throw new Error(
        "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요."
      );
    }

    throw new Error("카카오 로그인에 실패했습니다.");
  }
};

/**
 * 네이버 로그인 (리디렉션 방식)
 */
export const loginWithNaver = async (): Promise<void> => {
  try {
    await signInWithNaverRedirect();
    // 리디렉션 방식이므로 결과를 반환하지 않음
  } catch (error: any) {
    console.error("네이버 로그인 오류:", error);

    // Firebase Auth 에러 코드별 메시지
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("로그인 창이 닫혔습니다. 다시 시도해주세요.");
    } else if (error.code === "auth/popup-blocked") {
      throw new Error(
        "팝업이 차단되었습니다. 팝업을 허용하고 다시 시도해주세요."
      );
    } else if (error.code === "auth/network-request-failed") {
      throw new Error("네트워크 연결을 확인해주세요.");
    } else if (error.code === "auth/too-many-requests") {
      throw new Error(
        "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요."
      );
    }

    throw new Error("네이버 로그인에 실패했습니다.");
  }
};

/**
 * 모바일용 리다이렉트 로그인
 */
export const loginWithGoogleRedirect = () => signInWithGoogleRedirect();
export const loginWithKakaoRedirect = () => signInWithKakaoRedirect();
export const loginWithNaverRedirect = () => signInWithNaverRedirect();

/**
 * 리다이렉트 로그인 결과 처리
 */
export const handleRedirectResult =
  async (): Promise<SNSUserProfile | null> => {
    try {
      const auth = (
        await import("@/lib/api/firebase-ultra-safe")
      ).getFirebaseAuth();
      const result = await firebaseGetRedirectResult(auth);
      if (result && result.user) {
        const provider = result.providerId?.includes("google")
          ? "google"
          : result.providerId?.includes("kakao")
            ? "kakao"
            : "naver";
        return await handleSNSLogin(
          result.user,
          provider as "google" | "kakao" | "naver"
        );
      }
      return null;
    } catch (error) {
      console.error("리다이렉트 로그인 결과 처리 오류:", error);
      return null;
    }
  };
